import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import {
  Briefcase, FileCheck, FileText, BarChart3, MessageSquare,
  Import, Calculator, Home, LogOut, Globe,
  Newspaper, Layers, PieChart, Settings, History,
  ChevronLeft, ChevronRight, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import FloatingChatWidget from '@/pages/fo/FloatingChatWidget';

// ── Nav data ──────────────────────────────────────────────────────────────
const ASSET_CLASS_GROUPS = [
  {
    label: 'Public Markets',
    path: '/markets',
    permission: 'portfolios:read',
    items: [
      { label: 'Equities',       path: '/markets?tab=holdings&asset=stocks' },
      { label: 'Mutual Funds',   path: '/markets?tab=holdings&asset=mf' },
      { label: 'ETFs',           path: '/markets?tab=holdings&asset=etf' },
      { label: 'Debt',           path: '/markets?tab=holdings&asset=bonds' },
      { label: 'PMS',            path: '/markets?tab=holdings&asset=pms' },
      { label: 'AIF',            path: '/markets?tab=holdings&asset=aif' },
      { label: 'NPS',            path: '/markets?tab=holdings&asset=nps' },
      { label: 'Fixed Deposits', path: '/markets?tab=holdings&asset=fd' },
      { label: 'Gold / Silver',  path: '/markets?tab=holdings&asset=gold' },
      { label: 'Other Public Holdings',         path: '/markets?tab=others' },
    ],
  },
  {
    label: 'Private Markets',
    path: '/private-markets',
    permission: 'deals:read',
    items: [
      { label: 'Startups',         path: '/private-markets?tab=startups' },
      { label: 'PE / VC',          path: '/private-markets?tab=equity' },
      { label: 'Search Funds',     path: '/private-markets?tab=search' },
      { label: 'Long-Only Equity', path: '/private-markets?tab=long_only' },
      { label: 'Private Credit',   path: '/private-markets?tab=credit' },
      { label: 'Infrastructure',   path: '/private-markets?tab=infra' },
      { label: 'Other Private Holdings',           path: '/private-markets?tab=others' },
    ],
  },
  {
    label: 'Others',
    path: '/others',
    permission: 'real_estate:read',
    items: [
      { label: 'Real Estate',           path: '/others?tab=real_estate' },
      { label: 'Gold & Commodities',    path: '/others?tab=gold' },
      { label: 'Art & Collectibles',    path: '/others?tab=art' },
      { label: 'Cash & Equivalents',    path: '/others?tab=cash' },
      { label: 'Alternatives',          path: '/others?tab=alternatives' },
      { label: 'Intellectual Property', path: '/others?tab=ip' },
    ],
  },
];

const NAV_ITEMS_TOP = [
  { label: 'Dashboard',         icon: Home,          path: '/consolidated' },
  { label: 'Market Outlook',    icon: Newspaper,     path: '/market-outlook', permission: 'market_outlook:read' },
  { label: 'Portfolio Summary', icon: BarChart3,      path: '/analytics',     permission: 'analytics:read' },
];

const NAV_ITEMS_BOTTOM = [
  { label: 'Tax',              icon: Calculator,    path: '/tax',       permission: 'portfolios:read' },
  { label: 'Deal Pipeline',    icon: Layers,        path: '/deals',     permission: 'deals:read' },
  { label: 'Approvals',        icon: FileCheck,     path: '/approvals', permission: 'approvals:read' },
  { label: 'Generate Reports', icon: FileText,      path: '/reports',   permission: 'reports:read' },
  { label: 'OlafAI',           icon: MessageSquare, path: '/ai-chat',   permission: 'ai:access' },
];

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ASSET_PATHS = ASSET_CLASS_GROUPS.map(g => g.path);
const ASSET_SUB_PATHS = ASSET_CLASS_GROUPS.flatMap(g => g.items.map(i => i.path.split('?')[0]));

const ROLE_DISPLAY = {
  super_admin: 'Super Admin', admin: 'Administrator', principal: 'Principal',
  cio: 'Chief Investment Officer', investment_analyst: 'Investment Analyst', viewer: 'Viewer',
};

// ── Helpers ───────────────────────────────────────────────────────────────
function isAssetActive(pathname) {
  return ASSET_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
    || ASSET_SUB_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'));
}

function subItemActive(item, pathname, search) {
  const [subPath, subQuery] = item.path.split('?');
  if (!subQuery) return pathname === subPath || pathname.startsWith(subPath + '/');
  const params = new URLSearchParams(search);
  const exp    = new URLSearchParams(subQuery);
  const tabMatch   = params.get('tab') === exp.get('tab');
  const assetMatch = exp.get('asset') ? params.get('asset') === exp.get('asset') : !params.get('asset');
  return pathname === subPath && tabMatch && assetMatch;
}

// ── Asset Class Flyout ────────────────────────────────────────────────────
function AssetFlyout({ groups, onClose, permissions }) {
  const { pathname, search } = useLocation();
  const visibleGroups = groups.filter(g => !g.permission || permissions.includes(g.permission));

  return (
    <div
      className="flex flex-col h-screen overflow-y-auto shrink-0"
      style={{ width: '196px', backgroundColor: '#deeac2', borderRight: '1px solid #c5d6af' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 shrink-0"
        style={{ height: '64px', borderBottom: '1px solid #c5d6af' }}
      >
        <span className="text-xs font-bold text-[#404040]">Asset Classes</span>
        <button
          onClick={onClose}
          className="text-[#666] hover:text-[#1a1a1a] transition-colors"
          title="Close"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Groups */}
      <div className="flex-1 py-2">
        {visibleGroups.map(group => (
          <div key={group.path} className="mb-3">
            <Link
              to={group.path}
              className="block px-3 py-1 text-[11px] font-bold text-[#404040] hover:bg-[#c8d9b0] transition-colors"
            >
              {group.label}
            </Link>
            {group.items.map(item => {
              const active = subItemActive(item, pathname, search);
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`block px-3 py-[5px] text-[11px] transition-colors ${
                    active
                      ? 'bg-[#fcfcf5] text-[#404040] font-medium'
                      : 'text-[#555] hover:bg-[#c8d9b0] hover:text-[#1a1a1a]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Full expanded sidebar (256px) ─────────────────────────────────────────
function FullSidebar({ user, permissions, pathname, onLogout, onAssetClassClick, pendingIcMemoCount }) {
  const navigate    = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [subOpen, setSubOpen]           = useState({});
  const { search } = useLocation();

  const visibleTop    = NAV_ITEMS_TOP.filter(i => !i.permission || permissions.includes(i.permission));
  const visibleBottom = NAV_ITEMS_BOTTOM.filter(i => !i.permission || permissions.includes(i.permission));
  const visibleGroups = ASSET_CLASS_GROUPS.filter(g => !g.permission || permissions.includes(g.permission));
  const assetActive   = isAssetActive(pathname);

  const roleLabel = ROLE_DISPLAY[user?.role] || (user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User');

  return (
    <div
      className="flex flex-col h-screen bg-[#dae4c4] border-r border-[#c5d6af] shrink-0 overflow-hidden"
      style={{ width: '256px' }}
    >
      {/* Logo */}
      <div
        className="flex items-center justify-center relative shrink-0"
        style={{ height: '64px', borderBottom: '1px solid #c5d6af', overflow: 'hidden' }}
      >
        <img
          src="/vaia-icon.png"
          alt="VAIA"
          style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
          onError={e => { e.target.src = '/vaia-logo-white.png'; }}
        />
      </div>

      {/* Nav — no horizontal padding on container so items span full width */}
      <nav className="flex-1 py-2 space-y-0.5 overflow-y-auto overflow-x-hidden">
        {visibleTop.map(item => {
          const Icon = item.icon;
          const active = pathname === item.path || (item.path !== '/consolidated' && pathname.startsWith(item.path + '/'));
          return (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 py-2.5 text-sm border-l-4 ${
                active
                  ? 'bg-[#fcfcf5] text-[#1a1a1a] font-medium pl-3 pr-4'
                  : 'text-[#404040] hover:bg-[#c8d9b0] hover:text-[#1a1a1a] border-transparent pl-4 pr-4'
              }`}
              style={{ borderLeftColor: active ? '#ffd879' : 'transparent' }}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {item.label}
            </Link>
          );
        })}

        {/* Asset Classes */}
        {visibleGroups.length > 0 && (
          <button
            onClick={onAssetClassClick}
            className={`w-full flex items-center gap-3 py-2.5 text-sm border-l-4 ${
              assetActive
                ? 'bg-[#fcfcf5] text-[#1a1a1a] font-medium pl-3 pr-4'
                : 'text-[#404040] hover:bg-[#c8d9b0] hover:text-[#1a1a1a] border-transparent pl-4 pr-4'
            }`}
            style={{ borderLeftColor: assetActive ? '#ffd879' : 'transparent' }}
          >
            <PieChart className="w-4 h-4 shrink-0" />
            Asset Classes
          </button>
        )}

        {visibleBottom.map(item => {
          const Icon = item.icon;
          const active = pathname === item.path || pathname.startsWith(item.path + '/');
          return (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 py-2.5 text-sm border-l-4 ${
                active
                  ? 'bg-[#fcfcf5] text-[#1a1a1a] font-medium pl-3 pr-4'
                  : 'text-[#404040] hover:bg-[#c8d9b0] hover:text-[#1a1a1a] border-transparent pl-4 pr-4'
              }`}
              style={{ borderLeftColor: active ? '#ffd879' : 'transparent' }}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {item.label}
              {item.path === '/deals' && pendingIcMemoCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#ffd879] text-[#1a1a1a] text-[10px] font-semibold">
                  {pendingIcMemoCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="border-t border-[#c5d6af] shrink-0 relative">
        {settingsOpen && (
          <div className="absolute bottom-full mb-1 left-3 right-3 bg-white border border-[#c5d6af] z-50 rounded-md shadow-md">
            <button onClick={() => { navigate('/import'); setSettingsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#404040] hover:bg-[#e8f0d8] transition-colors">
              <Import className="w-4 h-4" />Data Import
            </button>
            {permissions?.includes('audit:read') && (
              <button onClick={() => { navigate('/audit-log'); setSettingsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#404040] hover:bg-[#e8f0d8] transition-colors">
                <History className="w-4 h-4" />Audit Log
              </button>
            )}
            <div className="border-t border-[#c5d6af]" />
            <button onClick={() => { setSettingsOpen(false); onLogout(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#404040] hover:bg-[#e8f0d8] transition-colors">
              <LogOut className="w-4 h-4" />Sign out
            </button>
          </div>
        )}
        <div className="flex items-center gap-3 px-3 py-3">
          <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center text-white text-xs font-semibold shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#404040] truncate">{user?.name || user?.full_name || user?.email?.split('@')[0] || 'User'}</p>
            <p className="text-xs text-[#666] truncate">{roleLabel}</p>
          </div>
          <button onClick={() => setSettingsOpen(o => !o)} className="text-[#666] hover:text-[#1a1a1a] transition-colors shrink-0">
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Icon strip (60px) ─────────────────────────────────────────────────────
function IconStrip({ user, permissions, pathname, onLogout, onExpand, onAssetClick, assetFlyoutOpen, pendingIcMemoCount }) {
  const navigate  = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const visibleTop    = NAV_ITEMS_TOP.filter(i => !i.permission || permissions.includes(i.permission));
  const visibleBottom = NAV_ITEMS_BOTTOM.filter(i => !i.permission || permissions.includes(i.permission));
  const assetActive   = isAssetActive(pathname);

  return (
    <div
      className="flex flex-col h-screen bg-[#dae4c4] border-r border-[#c5d6af] shrink-0 relative"
      style={{ width: '60px' }}
    >
      {/* Logo icon only */}
      <div
        className="flex items-center justify-center shrink-0"
        style={{ height: '64px', borderBottom: '1px solid #c5d6af' }}
      >
        <img
          src="/image.png"
          alt="VAIA"
          style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
          onError={e => { e.target.src = '/vaia-logo-white.png'; }}
        />
      </div>

      {/* Expand button */}
      <button
        onClick={onExpand}
        className="flex items-center justify-center h-8 text-[#666] hover:text-[#1a1a1a] hover:bg-[#c8d9b0] transition-colors shrink-0"
        title="Expand sidebar"
      >
        <PanelLeftOpen className="w-4 h-4" />
      </button>

      {/* Top nav icons */}
      <nav className="flex-1 flex flex-col py-1 overflow-y-auto overflow-x-hidden">
        {visibleTop.map(item => {
          const Icon = item.icon;
          const active = pathname === item.path || (item.path !== '/consolidated' && pathname.startsWith(item.path + '/'));
          return (
            <div key={item.path} className="relative">
              {/* yellow active indicator bar */}
              {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-sm" style={{ backgroundColor: '#ffd879' }} />}
              <Link to={item.path} title={item.label}
                className={`flex items-center justify-center h-10 transition-colors ${
                  active ? 'bg-[#fcfcf5]' : 'hover:bg-[#c8d9b0]'
                }`}
              >
                <Icon className="w-5 h-5 text-[#404040]" />
              </Link>
            </div>
          );
        })}

        {/* Asset Classes icon */}
        <div className="relative">
          {(assetFlyoutOpen || assetActive) && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-sm" style={{ backgroundColor: '#ffd879' }} />
          )}
          <button
            onClick={onAssetClick}
            title="Asset Classes"
            className={`w-full flex items-center justify-center h-10 transition-colors ${
              assetFlyoutOpen || assetActive ? 'bg-[#fcfcf5]' : 'hover:bg-[#c8d9b0]'
            }`}
          >
            <PieChart className="w-5 h-5 text-[#1a1a1a]" />
          </button>
        </div>

        {visibleBottom.map(item => {
          const Icon = item.icon;
          const active = pathname === item.path || pathname.startsWith(item.path + '/');
          return (
            <div key={item.path} className="relative">
              {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-sm" style={{ backgroundColor: '#ffd879' }} />}
              <Link to={item.path} title={item.label}
                className={`flex items-center justify-center h-10 transition-colors ${
                  active ? 'bg-[#fcfcf5]' : 'hover:bg-[#c8d9b0]'
                }`}
              >
                <Icon className="w-5 h-5 text-[#404040]" />
              </Link>
              {item.path === '/deals' && pendingIcMemoCount > 0 && (
                <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[15px] h-[15px] px-0.5 rounded-full bg-[#ffd879] text-[#1a1a1a] text-[9px] font-semibold">
                  {pendingIcMemoCount}
                </span>
              )}
            </div>
          );
        })}
      </nav>

      {/* User icon */}
      <div className="border-t border-[#c5d6af] shrink-0 relative">
        {settingsOpen && (
          <div className="absolute bottom-full mb-1 left-full ml-1 bg-white border border-[#c5d6af] z-50 rounded-md shadow-md w-40">
            <button onClick={() => { navigate('/import'); setSettingsOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#404040] hover:bg-[#e8f0d8] transition-colors">
              <Import className="w-3.5 h-3.5" />Data Import
            </button>
            {permissions?.includes('audit:read') && (
              <button onClick={() => { navigate('/audit-log'); setSettingsOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#404040] hover:bg-[#e8f0d8] transition-colors">
                <History className="w-3.5 h-3.5" />Audit Log
              </button>
            )}
            <div className="border-t border-[#c5d6af]" />
            <button onClick={() => { setSettingsOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#404040] hover:bg-[#e8f0d8] transition-colors">
              <LogOut className="w-3.5 h-3.5" />Sign out
            </button>
          </div>
        )}
        <button
          onClick={() => setSettingsOpen(o => !o)}
          className="w-full flex items-center justify-center h-12 hover:bg-[#c8d9b0] transition-colors"
          title={user?.name || 'User'}
        >
          <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center text-white text-xs font-semibold shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────
export default function FOLayout() {
  const { user, permissions, signOut } = useAuth();
  const location = useLocation();
  const navigate  = useNavigate();
  const pathname  = location.pathname;

  // 'full' = normal expanded sidebar
  // 'flyout' = icon strip + asset class flyout (only triggered by Asset Classes click)
  const [mode, setMode] = useState('full');
  const [pendingIcMemoCount, setPendingIcMemoCount] = useState(0);

  useEffect(() => {
    if (!permissions?.includes('deals:read')) return;
    axios.get(`${API}/deals/ic-memos`)
      .then(r => setPendingIcMemoCount((r.data || []).filter(m => ['submitted', 'under_review'].includes(m.status)).length))
      .catch(() => {});
  }, [permissions]);

  const handleOpenFlyout  = () => { setMode('flyout'); if (!isAssetActive(pathname)) navigate('/markets'); };
  const handleCloseFlyout = () => setMode('full');

  // Auto-return to full sidebar when navigating away from asset pages
  useEffect(() => {
    if (mode === 'flyout' && !isAssetActive(pathname)) {
      setMode('full');
    }
  }, [pathname]); // only re-run when pathname changes

  const showFull   = mode === 'full';
  const showFlyout = mode === 'flyout';

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {showFull && (
        <FullSidebar
          user={user}
          permissions={permissions || []}
          pathname={pathname}
          onLogout={signOut}
          onAssetClassClick={handleOpenFlyout}
          pendingIcMemoCount={pendingIcMemoCount}
        />
      )}
      {showFlyout && (
        <IconStrip
          user={user}
          permissions={permissions || []}
          pathname={pathname}
          onLogout={signOut}
          onExpand={handleCloseFlyout}
          onAssetClick={handleCloseFlyout}
          assetFlyoutOpen={true}
          pendingIcMemoCount={pendingIcMemoCount}
        />
      )}
      {showFlyout && (
        <AssetFlyout
          groups={ASSET_CLASS_GROUPS}
          permissions={permissions || []}
          onClose={handleCloseFlyout}
        />
      )}

      <main className="flex-1 overflow-y-auto relative">
        <Outlet />
        <FloatingChatWidget />
      </main>
    </div>
  );
}
